import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  PreferenciaCategoria,
  PreferenciasInsights,
  obterPreferencias,
  salvarPreferencias,
} from '../../services/insights';
import { useInsights } from './InsightsProvider';

const OPCOES: { valor: PreferenciaCategoria; rotulo: string }[] = [
  { valor: 'menos', rotulo: 'Menos' },
  { valor: 'normal', rotulo: 'Normal' },
  { valor: 'mais', rotulo: 'Mais' },
  { valor: 'desligado', rotulo: 'Desligar' },
];

const Caixa = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Secao = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TituloSecao = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2937;
`;

const Ajuda = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  margin-top: -6px;
`;

const Linha = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Nome = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.86rem;
  font-weight: 600;
  color: #1f2937;
`;

const Segmentos = styled.div`
  display: flex;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  overflow: hidden;
`;

const Segmento = styled.button<{ $ativo: boolean }>`
  height: 30px;
  padding: 0 9px;
  border: 0;
  font-size: 0.72rem;
  cursor: pointer;
  background: ${({ $ativo }) => ($ativo ? '#1F467E' : '#fff')};
  color: ${({ $ativo }) => ($ativo ? '#fff' : '#475569')};
  font-weight: ${({ $ativo }) => ($ativo ? 600 : 400)};
`;

const Interruptor = styled.button<{ $ligado: boolean }>`
  position: relative;
  width: 44px;
  height: 26px;
  border-radius: 13px;
  border: 0;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  background: ${({ $ligado }) => ($ligado ? '#1F467E' : '#94a3b8')};

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $ligado }) => ($ligado ? '21px' : '3px')};
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.15s;
  }
`;

const Aprendizado = styled.div`
  background: #e8eef7;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #1e3a66;
`;

const Voltar = styled.button`
  align-self: flex-start;
  background: none;
  border: 0;
  padding: 0;
  color: #1F467E;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
`;

/** Frase do que o assistente aprendeu, a partir das categorias com avaliações suficientes. */
export const resumoDoAprendizado = (preferencias: PreferenciasInsights | null): string => {
  const avaliadas = (preferencias?.categorias ?? []).filter(c => c.afinidade !== null && c.afinidade !== undefined);
  if (avaliadas.length === 0) {
    return 'Ainda estou conhecendo você. Use os botões de útil e não útil nos insights para eu acertar mais.';
  }
  const gosta = avaliadas.filter(c => (c.afinidade ?? 0) >= 0.6).map(c => c.nome.toLowerCase());
  const naoGosta = avaliadas.filter(c => (c.afinidade ?? 1) <= 0.4).map(c => c.nome.toLowerCase());
  const partes: string[] = [];
  if (gosta.length) partes.push(`Você costuma aproveitar os insights de ${gosta.join(', ')}, então eles aparecem antes.`);
  if (naoGosta.length) partes.push(`Os de ${naoGosta.join(', ')} você tem dispensado, então mostro menos deles.`);
  return partes.length ? partes.join(' ') : 'Até agora você aproveita os insights de forma parecida em todas as categorias.';
};

/** Preferências do assistente: quanto mostrar de cada categoria, o balão do dia e o que ele já aprendeu. */
export const PreferenciasAssistente: React.FC<{ aoVoltar: () => void }> = ({ aoVoltar }) => {
  const { recarregar } = useInsights();
  const [preferencias, setPreferencias] = useState<PreferenciasInsights | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    obterPreferencias().then(setPreferencias).catch(() => setErro(true));
  }, []);

  const salvar = (novas: PreferenciasInsights) => {
    setPreferencias(novas);
    salvarPreferencias(novas).then(() => recarregar()).catch(() => setErro(true));
  };

  if (erro) {
    return (
      <Caixa>
        <Voltar type="button" onClick={aoVoltar}>Voltar aos insights</Voltar>
        <Ajuda>Não consegui carregar suas preferências agora. Tente de novo em instantes.</Ajuda>
      </Caixa>
    );
  }
  if (!preferencias) return <Caixa><Ajuda>Carregando...</Ajuda></Caixa>;

  return (
    <Caixa>
      <Voltar type="button" onClick={aoVoltar}>Voltar aos insights</Voltar>

      <Secao>
        <TituloSecao>O que você quer ver</TituloSecao>
        <Ajuda>Avisos urgentes sempre aparecem, mesmo nas categorias desligadas.</Ajuda>
        {preferencias.categorias.map(c => (
          <Linha key={c.categoria}>
            <Nome>{c.nome}</Nome>
            <Segmentos role="group" aria-label={c.nome}>
              {OPCOES.map(o => (
                <Segmento
                  key={o.valor}
                  type="button"
                  $ativo={c.preferencia === o.valor}
                  aria-pressed={c.preferencia === o.valor}
                  onClick={() => salvar({
                    ...preferencias,
                    categorias: preferencias.categorias.map(x => (x.categoria === c.categoria ? { ...x, preferencia: o.valor } : x)),
                  })}
                >
                  {o.rotulo}
                </Segmento>
              ))}
            </Segmentos>
          </Linha>
        ))}
      </Secao>

      <Linha>
        <Nome>Balão com o destaque do dia</Nome>
        <Interruptor
          type="button"
          role="switch"
          aria-checked={preferencias.balaoAtivo}
          aria-label="Balão com o destaque do dia"
          $ligado={preferencias.balaoAtivo}
          onClick={() => salvar({ ...preferencias, balaoAtivo: !preferencias.balaoAtivo })}
        />
      </Linha>

      <Aprendizado>
        <strong>O que aprendi sobre você</strong>
        <div>{resumoDoAprendizado(preferencias)}</div>
      </Aprendizado>
    </Caixa>
  );
};
